import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { usePosting, useTitle } from '../../Hooks';
import { BusinessPosting, Location, EditPostingInfo, PostingStatus } from '../../Classes';
import { Header, Button, Input, Field, Table, Message, Image, Groupper, Toast } from 'react-frontier';
import { bindFormChange, bindClick } from '@tempu/components/Util';
import { Modal, Autocomplete, GoogleMap } from '../../components';
import { Icon } from 'semantic-ui-react';
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import API, { CDN_URL } from '../../API';
import MDEditor, { commands } from '@uiw/react-md-editor/nohighlight';
import Validator from '@tempu/components/Validator';
import BusinessAccess from '../../BusinessAccess';
import moment from 'moment';
import classNames from 'classnames';
import remarkGfm from 'remark-gfm';

enum Modals{
	INFO = 1,
	PUBLISH = 2,
	PAYMENT = 3,
	DESCRIPTION = 4
}

var Posting = ()=>{
	var params = useParams<{ bid: string, pid: string }>();
	var posting_id = parseInt(params?.pid);

	var { setTitle } = useTitle();
	var { hasAccess } = usePosting(posting_id);
	var [posting, setPosting] = useState<BusinessPosting>(null);
	var [location, setLocation] = useState<Location>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [editingLocation, setEditingLocation] = useState<boolean>(false);
	var [locationPrompts, setLocationPrompts] = useState<string[]>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [editInfoForm, setEditInfoForm] = useState<EditPostingInfo>(null);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var imageInputRef = useRef<HTMLInputElement>(null);
	var [addingImage, setAddingImage] = useState<boolean>(false);

	useEffect(()=>{
		if(!posting_id || Number.isNaN(posting_id)) return;
		if(!hasAccess(BusinessAccess.POSTINGS.VIEW_POSTINGS)) return;

		API.getEditPosting(posting_id, true, true).then(res=>{
			if(res.error) return setLoadError(res.message);
			setTitle(res.data.posting_name);
			setPosting(res.data);
		}).catch(err=>{
			return setLoadError('Hubo un error inesperado cargando la información del empleo. (LCL-1)');
		})
	}, []);

	var posting_image_refresh = useMemo(()=>{
		return moment().unix();
	}, [posting, addingImage]);
	
	var editLocation = async(setLoading: (l: boolean)=>void)=>{
		if(!location) return setLocationPrompts(['Favor de seleccionar la ubicación de la lista de opciones.']);
		setLoading(true);
		setLocationPrompts(null);
		API.editPostingLocation(posting_id, location).then(res=>{
			if(res.error) return setLocationPrompts([res.message]);
			Toast.success('Ubicación actualizada!');
			var p = {...posting};
			p.location = location;
			setPosting(p);
			setLocation(null);
			setEditingLocation(false);
		}).catch(err=>{
			setLocationPrompts(['Hubo un error al editar la ubicación. (PLE-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var openEditDataModal = ()=>{
		setEditInfoForm({
			posting_name: posting.posting_name,
			date_start: posting.date_start,
			date_end: posting.date_end,
			description: posting.description
		})
		setShownModal(Modals.INFO);
	};

	var openDescriptionModal = ()=>{
		setEditInfoForm({
			posting_name: posting.posting_name,
			date_start: posting.date_start,
			date_end: posting.date_end,
			description: posting.description
		})
		setShownModal(Modals.DESCRIPTION);
	}

	var editInfo = async(setLoading: (l: boolean)=>void)=>{
		if(editInfoForm.date_start > editInfoForm.date_end) return setModalPrompts(['La fecha fin debe ser mayor a la fecha inicio.']);
		var { valid, prompts } = Validator(editInfoForm, {  
			posting_name: [{
				rule: 'minLength', params: [3], label: 'Nombre de empleo'
			}],
			date_start: [{
				rule: 'empty', label: 'Fecha inicio'
			}],
			date_end: [{
				rule: 'empty', label: 'Fecha fin'
			}],
		});
		setModalPrompts(prompts);
		if(!valid) return;
		setLoading(true);
		API.editPostingInfo(posting_id, editInfoForm).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Empleo actualizado!');
			var p = {...posting};
			p.date_start = editInfoForm.date_start;
			p.date_end = editInfoForm.date_end;
			p.posting_name = editInfoForm.posting_name;
			p.description = editInfoForm.description;
			setPosting(p);
			setTitle(p.posting_name);
			setEditInfoForm(null);
			setShownModal(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error al editar el empleo. (PEE-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var changePublish = async(setLoading: (l: boolean)=>void)=>{
		setLoading(true);
		API.publishPosting(posting_id, !posting.published).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Empleo actualizado!');
			var p = {...posting};
			p.published = !p.published;
			setPosting(p);
			setShownModal(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error al modificar el empleo. (PEE-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var addImage = ()=>{
		imageInputRef.current?.click();
	}

	var handleImage = (ev: React.ChangeEvent<HTMLInputElement>)=>{
		if(!ev.target.files || ev.target.files.length==0) return;
		if(ev.target.files.length>1) return Toast.error("Favor de solo seleccionar 1 archivo.");
		var image = ev.target.files[0];
		if(image.size>(2.5*1024*1024)) return Toast.error('La imagen no puede pesar mas de 2.5MB.');

		setAddingImage(true);
		var lt = Toast.show('Subiendo imágen del empleo', {
			loading: true
		})
		API.addPostingImage(posting.posting_id, image).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.dismiss(lt);
			Toast.success('Se ha agregado la imágen del empleo');
		}).catch(err=>{
			return Toast.error('Hubo un error subiendo la imagen del empleo. (LCL-1)');
		}).finally(()=>{
			Toast.dismiss(lt);
			setAddingImage(false);
		});
	}

	var onEditInfo = bindFormChange(editInfoForm, setEditInfoForm);

	if(!hasAccess(BusinessAccess.POSTINGS.VIEW_POSTINGS)){
		return <Header text='Sin acceso' subtext='No tienes acceso a esta página' />
	}

	if(loadError){
		return <Header text='Error' subtext={loadError} />
	}

	if (!posting) {
		return <Header loading text='Cargando datos' />;
	}

	const NoInfo = <span style={{ fontStyle: 'italic', color: 'gray' }}>Sin información</span>
	var missing_info = !posting.location_id || posting.date_start===null || posting.job_count<=0 || posting.date_end===null;
	var posting_status = (
		missing_info ? PostingStatus.MISSING_INFO : 
		posting.approved ? (
			posting.published ? PostingStatus.PUBLISHED : PostingStatus.NOT_PUBLISHED
		) : PostingStatus.PENDING_APPROVAL
	);

	var PostingStatusLabel = (
		<div className={classNames("fr label", {
			green: posting_status===PostingStatus.PUBLISHED,
			orange: posting_status===PostingStatus.NOT_PUBLISHED || posting_status===PostingStatus.PENDING_APPROVAL,
			red: posting_status===PostingStatus.MISSING_INFO
		})}>
			{
				posting_status===PostingStatus.MISSING_INFO ? 'Información faltante' :
				posting_status===PostingStatus.PENDING_APPROVAL ? 'En aprobación' : 
				posting_status===PostingStatus.PUBLISHED ? 'Publicado' :
				posting_status===PostingStatus.NOT_PUBLISHED ? 'No publicado' : 
				'Cancelado'
			}
		</div>
	)

	return <div className='tp posting-info-screen'>
		<div className='posting-info'>
			<div className="image">
				<Image src={`${CDN_URL}/posting/${posting.posting_id}/large.webp?t=${posting_image_refresh}`} fallbackComponent={(
					<i className='image icon' />
				)} />
				<input className='image-button' type="file" style={{ display: 'none' }} ref={imageInputRef} accept="image/*" onChange={handleImage}/>
				{hasAccess(BusinessAccess.POSTINGS.EDIT_POSTING_IMAGE) && (
					<Button iconName={'pencil'} iconStyle={{ marginRight: 5 }} size='tiny' loading={addingImage} text='Cambiar imágen' style={{ marginTop: 5, lineHeight: '16px', width: 150 }} onClick={addImage}/>
				)}
			</div>
			<Table
				details
				striped
				title='Datos de empleo'
				className='info'
				button={hasAccess(BusinessAccess.POSTINGS.EDIT_POSTING) && 
					<Button text='Editar' iconName='pen' size='tiny' onClick={openEditDataModal} />
				}
			>
				<Table.Row data={['Nombre', posting.posting_name]}/>
				<Table.Row data={['Fecha inicio',posting.date_start ? moment.unix(posting.date_start).format('DD/MMM/YY HH:mm') : NoInfo]}/>
				<Table.Row data={['Fecha fin',posting.date_end ?  moment.unix(posting.date_end).format('DD/MMM/YY HH:mm') : NoInfo]}/>
				<Table.Row data={['Estatus', PostingStatusLabel]} />
				<Table.Row data={['Creación', moment.unix(posting.date_created).format('DD/MMM/YY HH:mm')]}/>
			</Table>
		</div>
		{posting_status!==PostingStatus.PUBLISHED && (
			<Message style={{ maxWidth: 500, margin: '10px auto', marginTop: 10 }} type={posting_status===PostingStatus.NOT_PUBLISHED ? 'info' : (posting_status===PostingStatus.PENDING_APPROVAL ? 'orange' : 'error')} centered>
				{posting_status===PostingStatus.NOT_PUBLISHED ? <>
					<div className="header">No publicado</div>
					El empleo se encuentra listo para ser publicado. <br />
					<Button text='Publicar empleo' color='blue' onClick={()=>setShownModal(Modals.PUBLISH)} />
				</> : posting_status===PostingStatus.PENDING_APPROVAL ? <>
					<div className="header">Pendiente de aprobación</div>
					El equipo de Ebentu esta realizando la autorización de tu empleo. <br />
					Por mientras podrás editarlo a tu placer.
				</> : posting_status===PostingStatus.MISSING_INFO ? <>
					<div className="header">Información faltante</div>
					<ul className="list">
						{(posting.date_start===null || posting.date_end===null) && (
							<li>Fechas del empleo faltantes</li>
						)}
						{posting.job_count<=0 && (
							<li>No hay puestos del empleo publicados</li>
						)}
					</ul>
				</> : null}
			</Message>
		)}
		<Groupper style={{ marginBottom: 15, marginTop: 15 }}>
			<div className="head">
				<div className="left">Descripción</div>
				{hasAccess(BusinessAccess.POSTINGS.EDIT_POSTING) && (
					<Button text='Editar' iconName='pen' size='tiny' onClick={openDescriptionModal} />
				)}
			</div>
			<div className="content">
				{posting.description ? (
					<ReactMarkdown linkTarget={'_blank'} remarkPlugins={[remarkGfm]} children={posting.description} />
				) : (
					<Header iconName='pen' text={'Sin descripción'} subtext={'Agrega una descripción general del empleo'} size='small' actions={hasAccess(BusinessAccess.POSTINGS.EDIT_POSTING) && (
						<Button text='Editar' onClick={openDescriptionModal} />
					)} />
				)}
			</div>
		</Groupper>
		<Groupper fitted className='location' style={{ marginTop: 10 }}>
			<div className="head">
				<div className="left">Ubicación</div>
				{hasAccess(BusinessAccess.POSTINGS.EDIT_POSTING_LOCATION) ? 
					(!editingLocation ? (
						<Button 
							text='Editar' 
							iconName='pen' 
							size='tiny' 
							onClick={bindClick(setEditingLocation, true)} 
						/> 
					) : (
						<Button 
							text='Cancelar' 
							color='black' 
							iconName='arrow-left' 
							size='tiny' 
							onClick={bindClick(setEditingLocation, false)} 
						/> 
				)) : null}
			</div>
			<Header text='Ubicación de empleo' subtext='Esta ubicación le aparecerá a cualquier usuario' />
			{(posting.location && !editingLocation && posting.location?.lattitude && posting.location?.longitude) ? (
				<GoogleMap className="map-embed" location={posting.location} />
			) : <div className='edit'>
				<Autocomplete placeholder='Buscar dirección' onPlaceSelected={setLocation} />
				<Message type='error' list={locationPrompts} />
				<Button className='save' text='Guardar' color='black' onClick={editLocation} />
			</div>}
		</Groupper>
		<Modal open={shownModal===Modals.PUBLISH} onClose={bindClick(setShownModal)} size='tiny'>
			<Modal.Header>{posting.published ? 'Despublicar' : 'Publicar'} empleo</Modal.Header>
			<Modal.Content>
				<Header text={`${posting.published ? 'Despublicar' : 'Publicar'} empleo`} subtext={!posting.published ? 'Se permitirá que los usuarios se postulen para el empleo.' : 'Se detendrá la postulación de usuarios para el empleo.'} />
				<Table
					details
					title='Empleo'
					data={[
						['ID', posting.posting_id],
						['Nombre', posting.posting_name],
						['Estatus', PostingStatusLabel]
					]}
				/>
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
				<Button text={posting.published ? 'Despublicar' : 'Publicar'} color='black' onClick={changePublish} />
			</Modal.Actions>
		</Modal>
		{hasAccess(BusinessAccess.POSTINGS.EDIT_POSTING) && (
			<Modal open={shownModal===Modals.INFO} onClose={bindClick(setShownModal)} size='tiny'>
				<Modal.Header>Modificar información</Modal.Header>
				{editInfoForm && <Modal.Content>
					<Input label='Nombre de empleo' placeholder='Ingresa el nombre de empleo' value={editInfoForm.posting_name} onChange={onEditInfo('posting_name')}/>
					<Field amount={2}>
						<Input 
							label='Fecha inicio' 
							style={{ width: '100%'}} 
							value={editInfoForm.date_start} 
							onChange={onEditInfo('date_start')} 
							calendar={{ 
								date: editInfoForm.date_start || moment().unix(), 
								mode: 'datetime', 
							}} />
						<Input 
							label='Fecha fin' 
							style={{ width: '100%'}} 
							value={editInfoForm.date_end} 
							onChange={onEditInfo('date_end')} 
							calendar={{ 
								date: editInfoForm.date_end || moment().unix(), 
								mode: 'datetime', 
							}} />
					</Field>
					{posting.published ? <div style={{ textAlign: 'center', marginTop: 15 }}><Button text={'Despublicar'} basic onClick={() => setShownModal(Modals.PUBLISH)} /></div> : null}
					<Message type="error" list={modalPrompts} />
				</Modal.Content>}
				<Modal.Actions>
					<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
					<Button text='Guardar' color='black' onClick={editInfo}/>
				</Modal.Actions>
			</Modal>
		)}
		{hasAccess(BusinessAccess.POSTINGS.EDIT_POSTING) && (
			<Modal open={shownModal===Modals.DESCRIPTION} onClose={bindClick(setShownModal)} size='small'>
				<Modal.Header>Editar descripción</Modal.Header>
				{editInfoForm && <Modal.Content>
					<Field label='Descripción'>
						<MDEditor 
							preview='edit'
							style={{ borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}
							value={editInfoForm.description} 
							onChange={onEditInfo('description')} 
							height={400}
							data-color-mode='light'
							extraCommands={[{
								...commands.codeEdit,
								icon: <Icon name='pencil' style={{ margin: 0, fontSize: 18 }} />
							}, {
								...commands.codePreview,
								icon: <Icon name='eye' style={{ margin: 0, fontSize: 18 }} />
							}]}
							commands={[
								commands.bold,
								commands.italic,
								commands.strikethrough,
								commands.divider,
								commands.hr,
								commands.quote,
								commands.code,
								commands.divider,
								commands.unorderedListCommand,
								commands.orderedListCommand,
							]}
						/>	
					</Field>
					<Message type="error" list={modalPrompts} />
				</Modal.Content>}
				<Modal.Actions>
					<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
					<Button text='Guardar' color='black' onClick={editInfo}/>
				</Modal.Actions>
			</Modal>
		)}
	</div>
}

export default Posting;